import { useState } from 'react';
import { PaymentMethod, PaymentMethodLabel, StoreSeller } from '../../models';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { ImageWithFallback } from '../../components/ui/ImageWithFallback';
import { formatCurrency } from '../../utils/currency';
import { getPixPrice, getInstallmentInfo, InstallmentInfo } from '../../utils/pricing';
import { usePaymentSettings } from '../../hooks/usePaymentSettings';
import { CartItem } from '../../models/cart';
import { CurrencyInput } from './CurrencyInput';
import { ALL_PAYMENT_METHODS, PaymentEntry, PaymentMode } from './types';

export function PaymentMethodIcon({ method }: { method: PaymentMethod }) {
  switch (method) {
    case PaymentMethod.Pix:
      return (
        <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="currentColor">
          <path d="M12 2L4 7v10l8 5 8-5V7z" />
        </svg>
      );
    case PaymentMethod.CreditCard:
    case PaymentMethod.DebitCard:
      return (
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="2" y="5" width="20" height="14" rx="2" strokeWidth={2} />
          <path strokeLinecap="round" strokeWidth={2} d="M2 10h20" />
        </svg>
      );
    case PaymentMethod.Cash:
      return (
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="2" y="6" width="20" height="12" rx="2" strokeWidth={2} />
          <circle cx="12" cy="12" r="3" strokeWidth={2} />
        </svg>
      );
    case PaymentMethod.BankTransfer:
      return (
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      );
    case PaymentMethod.CreditStore:
    case PaymentMethod.Promissory:
      return (
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    default:
      return null;
  }
}

export function PaymentCoverageBar({ total, paid }: { total: number; paid: number }) {
  const percentage = total > 0 ? Math.min((paid / total) * 100, 100) : 0;
  const isCovered = Math.abs(total - paid) < 0.01;
  const isOver = paid > total + 0.01;

  const barColor = isCovered ? 'bg-olivine' : isOver ? 'bg-warning' : 'bg-chocolate';

  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-text-medium">
          R$ {formatCurrency(paid)} de R$ {formatCurrency(total)}
        </span>
        <span className={`font-bold ${isCovered ? 'text-olivine' : isOver ? 'text-warning' : 'text-chocolate'}`}>
          {Math.round(percentage)}%
        </span>
      </div>
      <div className="w-full h-2.5 bg-champagne-dark/40 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${barColor}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
}

function PaymentEntryCard({
  entry,
  index,
  totalEntries,
  availableMethods,
  paymentRemainder,
  onUpdate,
  onRemove,
  onFillRemainder,
}: {
  entry: PaymentEntry;
  index: number;
  totalEntries: number;
  availableMethods: PaymentMethod[];
  paymentRemainder: number;
  onUpdate: (i: number, field: keyof PaymentEntry, value: number | PaymentMethod) => void;
  onRemove: (i: number) => void;
  onFillRemainder: (i: number) => void;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-champagne-dark/60 overflow-hidden animate-slide-in">
      <div className="flex items-center justify-between px-4 py-2.5 bg-champagne/60 border-b border-champagne-dark/30">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-chocolate text-white flex items-center justify-center text-xs font-bold">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-text-dark">
            Pagamento {index + 1}
          </span>
        </div>
        {totalEntries > 1 && (
          <button
            onClick={() => onRemove(index)}
            className="p-1.5 text-text-light hover:text-danger hover:bg-danger/10 rounded-lg transition-colors"
            aria-label="Remover pagamento"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      <div className="p-4">
        <label className="block text-xs font-medium text-text-medium mb-2">Forma de pagamento</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          {availableMethods.map((pm) => {
            const isSelected = pm === entry.paymentMethod;
            return (
              <button
                key={pm}
                onClick={() => onUpdate(index, 'paymentMethod', pm)}
                className={[
                  'border rounded-xl px-3 py-3 flex items-center gap-2.5 transition-all cursor-pointer min-h-[48px]',
                  isSelected
                    ? 'bg-chocolate text-white border-chocolate shadow-md'
                    : 'bg-white text-text-medium border-champagne-dark hover:border-chocolate hover:text-chocolate hover:bg-champagne/30',
                ].join(' ')}
              >
                <span className={[
                  'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                  isSelected ? 'bg-white/20' : 'bg-champagne',
                ].join(' ')}>
                  <PaymentMethodIcon method={pm} />
                </span>
                <span className={`text-sm ${isSelected ? 'font-semibold' : 'font-medium'} leading-tight`}>
                  {PaymentMethodLabel[pm]}
                </span>
              </button>
            );
          })}
        </div>

        {entry.paymentMethod === PaymentMethod.CreditCard && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-text-medium mb-2">Parcelamento</label>
            <div className="flex gap-2">
              {([1, 2] as const).map((n) => (
                <button
                  key={n}
                  onClick={() => onUpdate(index, 'installments', n)}
                  className={[
                    'flex-1 border rounded-xl py-2.5 text-sm font-medium transition-all min-h-[44px]',
                    (entry.installments ?? 1) === n
                      ? 'bg-chocolate text-white border-chocolate'
                      : 'bg-white text-text-medium border-champagne-dark hover:border-chocolate hover:text-chocolate',
                  ].join(' ')}
                >
                  {n === 1 ? 'À vista' : `2x de R$ ${formatCurrency(entry.amountPaid / 2)}`}
                </button>
              ))}
            </div>
          </div>
        )}

        <label className="block text-xs font-medium text-text-medium mb-2">Valor</label>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-light font-medium">R$</span>
            <div className="pl-9">
              <CurrencyInput
                value={entry.amountPaid}
                onChange={(v) => onUpdate(index, 'amountPaid', v)}
              />
            </div>
          </div>
          {Math.abs(paymentRemainder) > 0.01 && (
            <button
              onClick={() => onFillRemainder(index)}
              className="text-sm text-chocolate border border-chocolate rounded-xl px-4 py-2.5 whitespace-nowrap hover:bg-champagne transition-colors font-medium min-h-[44px] w-full sm:w-auto"
            >
              Completar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CombinePaymentSelector({
  selected,
  onSelect,
  pixPrice,
  installmentInfo,
}: {
  selected: PaymentMethod;
  onSelect: (method: PaymentMethod) => void;
  pixPrice: number | null;
  installmentInfo: InstallmentInfo | null;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {ALL_PAYMENT_METHODS.map((pm) => {
        const isSelected = pm === selected;
        return (
          <button
            key={pm}
            onClick={() => onSelect(pm)}
            className={[
              'border rounded-xl px-3 py-3 flex items-center gap-2.5 transition-all cursor-pointer min-h-[48px]',
              isSelected
                ? 'bg-chocolate text-white border-chocolate shadow-md'
                : 'bg-white text-text-medium border-champagne-dark hover:border-chocolate hover:text-chocolate hover:bg-champagne/30',
            ].join(' ')}
          >
            <span className={[
              'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
              isSelected ? 'bg-white/20' : 'bg-champagne',
            ].join(' ')}>
              <PaymentMethodIcon method={pm} />
            </span>
            <span className="flex flex-col items-start leading-tight">
              <span className={`text-sm ${isSelected ? 'font-semibold' : 'font-medium'} leading-tight`}>
                {PaymentMethodLabel[pm]}
              </span>
              {pm === PaymentMethod.Pix && isSelected && pixPrice !== null && (
                <span className="text-[11px] font-semibold text-olivine-light mt-0.5">
                  R$ {formatCurrency(pixPrice)} no Pix
                </span>
              )}
              {pm === PaymentMethod.CreditCard && installmentInfo && (
                <span className={`text-[11px] mt-0.5 ${isSelected ? 'text-white/80' : 'text-text-light'}`}>
                  ou {installmentInfo.count}x de R$ {formatCurrency(installmentInfo.amount)} sem juros
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SellerPicker({
  sellers,
  loading,
  selectedSeller,
  onSelect,
}: {
  sellers: StoreSeller[];
  loading: boolean;
  selectedSeller: StoreSeller | null;
  onSelect: (seller: StoreSeller) => void;
}) {
  return (
    <div className="mb-5">
      <p className="text-[11px] font-bold uppercase tracking-wider text-chocolate-dark mb-2">Vendedora</p>

      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      ) : sellers.length === 0 ? (
        <EmptyState title="Nenhuma vendedora disponível no momento." />
      ) : (
        <div className="flex flex-col gap-2">
          {sellers.map((seller) => {
            const isSelected = selectedSeller?.id === seller.id;
            return (
              <button
                key={seller.id}
                type="button"
                onClick={() => onSelect(seller)}
                className={[
                  'w-full text-left bg-white rounded-xl p-3 shadow-sm flex items-center justify-between gap-3 border-2 transition-all',
                  isSelected ? 'border-chocolate' : 'border-transparent hover:border-champagne-dark',
                ].join(' ')}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-champagne flex items-center justify-center text-chocolate font-bold text-sm flex-shrink-0">
                    {seller.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-dark">{seller.name}</p>
                    <p className="text-xs text-text-light">{seller.whatsAppPhone}</p>
                  </div>
                </div>
                {isSelected && (
                  <svg className="w-5 h-5 text-chocolate flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrderItemsList({ items }: { items: CartItem[] }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-chocolate-dark mb-2.5">Seu pedido</p>
      <div className="flex flex-col gap-2.5 max-h-56 overflow-y-auto pr-1">
        {items.map((item) => {
          const unitPrice = item.promotionalPrice ?? item.price;
          return (
            <div key={`${item.productId}-${item.variantId}`} className="flex items-center gap-2.5">
              <ImageWithFallback
                src={item.imageUrl}
                alt={item.productName}
                className="w-8 h-8 rounded-lg flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-text-dark truncate">{item.productName}</p>
                <p className="text-[10px] text-text-light truncate">
                  {item.variantLabel} · {item.quantity}x
                </p>
              </div>
              <span className="text-xs font-semibold text-text-dark flex-shrink-0">
                R$ {formatCurrency(unitPrice * item.quantity)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TotalCard({ total, items }: { total: number; items: CartItem[] }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-champagne-dark border-t-4 border-t-chocolate">
      <OrderItemsList items={items} />
      <div className="border-t border-champagne-dark/30 my-3" />
      <div className="flex justify-between items-center">
        <span className="text-sm text-text-medium">Total do pedido</span>
        <span className="text-lg font-bold text-text-dark">R$ {formatCurrency(total)}</span>
      </div>
    </div>
  );
}

function CoverageCard({
  total,
  totalPaid,
  paymentRemainder,
  isCovered,
  isOver,
  items,
}: {
  total: number;
  totalPaid: number;
  paymentRemainder: number;
  isCovered: boolean;
  isOver: boolean;
  items: CartItem[];
}) {
  const friso = isCovered ? 'border-t-olivine' : isOver ? 'border-t-warning' : 'border-t-chocolate';

  return (
    <div className={`bg-white rounded-xl p-4 shadow-sm border border-champagne-dark border-t-4 transition-colors duration-300 ${friso}`}>
      <OrderItemsList items={items} />
      <div className="border-t border-champagne-dark/30 my-3" />
      <div className="flex justify-between items-center">
        <span className="text-sm text-text-medium">Total do pedido</span>
        <span className="text-lg font-bold text-text-dark">R$ {formatCurrency(total)}</span>
      </div>
      <PaymentCoverageBar total={total} paid={totalPaid} />
      {isCovered && (
        <div className="flex items-center gap-2 mt-3 text-olivine">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-semibold">Valor total coberto!</span>
        </div>
      )}
      {isOver && (
        <div className="flex items-center gap-2 mt-3 text-warning">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">
            Valor excede o total em R$ {formatCurrency(Math.abs(paymentRemainder))}
          </span>
        </div>
      )}
      {!isCovered && !isOver && paymentRemainder > 0 && (
        <p className="text-xs text-text-light mt-2">
          Faltam <span className="font-bold text-chocolate">R$ {formatCurrency(paymentRemainder)}</span> para cobrir o total
        </p>
      )}
    </div>
  );
}

interface MobileOrderBarCoverage {
  totalPaid: number;
  paymentRemainder: number;
  isCovered: boolean;
  isOver: boolean;
}

function MobileOrderBar({
  items,
  total,
  coverage,
  onBack,
  onNext,
  canProceed,
}: {
  items: CartItem[];
  total: number;
  coverage?: MobileOrderBarCoverage;
  onBack?: () => void;
  onNext: () => void;
  canProceed: boolean;
}) {
  const [itemsExpanded, setItemsExpanded] = useState(false);

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white border-t-4 border-t-chocolate shadow-[0_-8px_20px_-14px_rgba(60,36,21,0.4)]"
      style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
    >
      <div className={`overflow-hidden transition-all duration-300 ${itemsExpanded ? 'max-h-64' : 'max-h-0'}`}>
        <div className="max-h-64 overflow-y-auto px-4 pt-3">
          <OrderItemsList items={items} />
        </div>
      </div>

      <button
        type="button"
        onClick={() => setItemsExpanded((v) => !v)}
        className="w-full flex items-center justify-center gap-1.5 px-4 pt-2.5 pb-1 text-xs font-semibold text-chocolate"
      >
        Ver {items.length} {items.length === 1 ? 'item' : 'itens'} do pedido
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-200 ${itemsExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className="px-4 pb-3 pt-1">
        {coverage && (
          <div className="mb-2">
            <PaymentCoverageBar total={total} paid={coverage.totalPaid} />
            {coverage.isCovered && (
              <p className="text-xs font-semibold text-olivine mt-1.5">Valor total coberto!</p>
            )}
            {coverage.isOver && (
              <p className="text-xs font-medium text-warning mt-1.5">
                Valor excede o total em R$ {formatCurrency(Math.abs(coverage.paymentRemainder))}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Voltar"
              className="w-9 h-9 rounded-full border border-champagne-dark flex items-center justify-center text-text-medium hover:bg-champagne transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="flex-shrink-0">
            <p className="text-[10px] text-text-light leading-none mb-0.5">Total</p>
            <p className="text-base font-bold text-text-dark leading-none">R$ {formatCurrency(total)}</p>
          </div>
          <Button onClick={onNext} disabled={!canProceed} className="flex-1">
            Continuar
          </Button>
        </div>
      </div>
    </div>
  );
}

interface PaymentStepProps {
  sellers: StoreSeller[];
  loadingSellers: boolean;
  selectedSeller: StoreSeller | null;
  onSelectSeller: (seller: StoreSeller) => void;
  items: CartItem[];
  total: number;
  paymentEntries: PaymentEntry[];
  paymentRemainder: number;
  paymentMode: PaymentMode;
  onSelectCombineMethod: (method: PaymentMethod) => void;
  onSwitchToDetailed: () => void;
  onSwitchToCombine: () => void;
  onAddPaymentMethod: () => void;
  onRemove: (i: number) => void;
  onUpdate: (i: number, field: keyof PaymentEntry, value: number | PaymentMethod) => void;
  onFillRemainder: (i: number) => void;
  onBack?: () => void;
  onNext: () => void;
  isValid: boolean;
}

export function PaymentStep({
  sellers,
  loadingSellers,
  selectedSeller,
  onSelectSeller,
  items,
  total,
  paymentEntries,
  paymentRemainder,
  paymentMode,
  onSelectCombineMethod,
  onSwitchToDetailed,
  onSwitchToCombine,
  onAddPaymentMethod,
  onRemove,
  onUpdate,
  onFillRemainder,
  onBack,
  onNext,
  isValid,
}: PaymentStepProps) {
  const canProceed = isValid && !!selectedSeller;
  const { settings: paymentSettings } = usePaymentSettings();
  const pixPrice = paymentSettings ? getPixPrice(total, paymentSettings.pixDiscountPercentage) : null;
  const installmentInfo = paymentSettings ? getInstallmentInfo(total, paymentSettings) : null;

  if (paymentMode === 'combine') {
    const selectedMethod = paymentEntries[0]?.paymentMethod ?? PaymentMethod.Pix;

    return (
      <>
        <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-6 lg:items-start">
          <div className="pb-28 lg:pb-0">
            <SellerPicker sellers={sellers} loading={loadingSellers} selectedSeller={selectedSeller} onSelect={onSelectSeller} />

            <div className="flex items-start gap-2.5 sm:gap-3 bg-champagne rounded-xl p-3 sm:p-3.5 mb-4">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-text-dark">Escolha a forma de pagamento</p>
                <p className="text-xs text-text-medium mt-0.5">
                  Escolha a forma de pagamento que será finalizada com a vendedora.
                </p>
              </div>
            </div>

            <CombinePaymentSelector
              selected={selectedMethod}
              onSelect={onSelectCombineMethod}
              pixPrice={pixPrice}
              installmentInfo={installmentInfo}
            />

            <button
              type="button"
              onClick={onSwitchToDetailed}
              className="text-sm text-chocolate underline hover:text-chocolate-dark transition-colors mt-4 mb-4"
            >
              Quero dividir em mais de uma forma de pagamento
            </button>

            <div className="hidden lg:flex gap-3">
              {onBack && (
                <Button variant="outline" onClick={onBack} className="flex-1">
                  Voltar
                </Button>
              )}
              <Button onClick={onNext} disabled={!canProceed} className="flex-1">
                Continuar
              </Button>
            </div>
          </div>

          <div className="hidden lg:block lg:sticky lg:top-20">
            <TotalCard total={total} items={items} />
          </div>
        </div>

        <MobileOrderBar items={items} total={total} onBack={onBack} onNext={onNext} canProceed={canProceed} />
      </>
    );
  }

  const usedMethods = new Set(paymentEntries.map((e) => e.paymentMethod));
  const canAddMore = usedMethods.size < ALL_PAYMENT_METHODS.length;
  const totalPaid = paymentEntries.reduce((sum, e) => sum + e.amountPaid, 0);
  const isCovered = Math.abs(paymentRemainder) < 0.01;
  const isOver = paymentRemainder < -0.01;

  return (
    <>
      <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-6 lg:items-start">
        <div className="pb-28 lg:pb-0">
          <SellerPicker sellers={sellers} loading={loadingSellers} selectedSeller={selectedSeller} onSelect={onSelectSeller} />

          <div className="flex items-start gap-2.5 sm:gap-3 bg-champagne rounded-xl p-3 sm:p-3.5 mb-4">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-text-dark">Combine formas de pagamento</p>
              <p className="text-xs text-text-medium mt-0.5">
                Divida o valor do pedido entre Pix, cartão, dinheiro e mais.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 mb-4">
            {paymentEntries.map((entry, idx) => {
              const availableMethods = ALL_PAYMENT_METHODS.filter(
                (pm) => pm === entry.paymentMethod || !usedMethods.has(pm)
              );
              return (
                <PaymentEntryCard
                  key={idx}
                  entry={entry}
                  index={idx}
                  totalEntries={paymentEntries.length}
                  availableMethods={availableMethods}
                  paymentRemainder={paymentRemainder}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                  onFillRemainder={onFillRemainder}
                />
              );
            })}
          </div>

          {canAddMore && (
            <button
              onClick={onAddPaymentMethod}
              className="w-full border-2 border-dashed border-chocolate/30 rounded-xl py-5 px-4 flex flex-col items-center justify-center gap-2 text-chocolate hover:border-chocolate hover:bg-champagne/50 transition-all mb-4 group"
            >
              <span className="w-10 h-10 rounded-full bg-champagne group-hover:bg-chocolate group-hover:text-white text-chocolate flex items-center justify-center text-xl font-bold transition-all duration-200">
                +
              </span>
              <span className="text-sm font-semibold">Adicionar outra forma de pagamento</span>
              <span className="text-xs text-text-light">Divida o valor entre diferentes meios</span>
            </button>
          )}

          <div className="text-center mb-4">
            <button
              type="button"
              onClick={onSwitchToCombine}
              className="text-sm text-chocolate underline hover:text-chocolate-dark transition-colors"
            >
              Voltar para pagamento simples
            </button>
          </div>

          <div className="hidden lg:flex gap-3">
            {onBack && (
              <Button variant="outline" onClick={onBack} className="flex-1">
                Voltar
              </Button>
            )}
            <Button onClick={onNext} disabled={!canProceed} className="flex-1">
              Continuar
            </Button>
          </div>
        </div>

        <div className="hidden lg:block lg:sticky lg:top-20">
          <CoverageCard total={total} totalPaid={totalPaid} paymentRemainder={paymentRemainder} isCovered={isCovered} isOver={isOver} items={items} />
        </div>
      </div>

      <MobileOrderBar
        items={items}
        total={total}
        coverage={{ totalPaid, paymentRemainder, isCovered, isOver }}
        onBack={onBack}
        onNext={onNext}
        canProceed={canProceed}
      />
    </>
  );
}
